import { RecordContextProvider, useListContext, useTranslate } from "ra-core";
import { InfiniteList } from "@/components/admin/infinite-list";
import { DateField } from "@/components/admin";
import { cn } from "@/lib/utils";

import MobileHeader from "../layout/MobileHeader";
import { MobileContent } from "../layout/MobileContent";
import { InfinitePagination } from "../misc/InfinitePagination";
import { ActiveFilterBar } from "../misc/ActiveFilterBar";
import { useZoomMeetingFilterBar } from "./zoomMeetingFilterBar";
import type { ZoomMeetingSummaryView } from "../types";
import { MeetingAssetIcons } from "./MeetingAssetIcons";
import { refetchWhileAwaiting, rowsFromQueryData } from "./meetingAssets";
import { ReviewStatusField } from "./ReviewStatusField";
import { TranscriptExcerpt } from "./TranscriptExcerpt";
import { TranscriptMatchesProvider } from "./useTranscriptMatches";
import { meetingDisplayTitle, isGenericTopic } from "./meetingDisplayTitle";

export const MobileZoomMeetingList = () => {
  const translate = useTranslate();
  return (
    <>
      <MobileHeader>
        <h1 className="text-xl font-semibold">
          {translate("resources.zoom_meetings.name", { smart_count: 2 })}
        </h1>
      </MobileHeader>
      <MobileContent>
        <InfiniteList
          actions={false}
          filterDefaultValues={{ "has_content@eq": true }}
          sort={{ field: "meeting_start_time", order: "DESC" }}
          perPage={25}
          queryOptions={{
            refetchInterval: (query) =>
              refetchWhileAwaiting(
                rowsFromQueryData<ZoomMeetingSummaryView>(query.state.data),
              ),
          }}
        >
          <MobileZoomMeetingListContent />
        </InfiniteList>
      </MobileContent>
    </>
  );
};

const MobileZoomMeetingListContent = () => {
  const { data, isPending } = useListContext<ZoomMeetingSummaryView>();
  const translate = useTranslate();
  // No filter UI on mobile, but the list shares the desktop filters, so show
  // them (and let them be removed) even while the first page loads.
  const filterBar = (
    <ActiveFilterBar {...useZoomMeetingFilterBar()} className="mb-3" />
  );

  if (isPending) return filterBar;

  return (
    <TranscriptMatchesProvider>
      {filterBar}
      <div className="divide-y">
        {data?.map((meeting) => {
          const topic = (meeting.meeting_topic ?? "").trim();
          return (
            <RecordContextProvider key={meeting.id} value={meeting}>
              {/* Not a link: the mobile app registers no meeting page
              (CRM.tsx, MobileAdmin), so /zoom_meetings/:id/show is a dead
              route there. Link the row again once a mobile page exists. */}
              <div className="flex items-center gap-3 py-3 first:pt-0 last:pb-0 text-foreground -mx-4 px-4">
                <div className="flex-1 min-w-0">
                  <div
                    className={cn(
                      "text-sm font-medium truncate",
                      !meeting.custom_title?.trim() &&
                        isGenericTopic(topic) &&
                        "italic text-muted-foreground",
                    )}
                  >
                    {meetingDisplayTitle(meeting)}
                  </div>
                  <TranscriptExcerpt meetingId={meeting.id} />
                  <div className="flex items-center gap-2 mt-0.5 text-xs text-muted-foreground">
                    <DateField
                      source="meeting_start_time"
                      options={{
                        month: "short",
                        day: "numeric",
                        hour: "2-digit",
                        minute: "2-digit",
                        hour12: false,
                      }}
                    />
                    <MeetingAssetIcons record={meeting} size="sm" />
                  </div>
                </div>
                <ReviewStatusField />
              </div>
            </RecordContextProvider>
          );
        })}
        {(!data || data.length === 0) && (
          <div className="p-4 text-muted-foreground text-sm">
            {translate("resources.zoom_meetings.empty", {
              _: "No meetings found.",
            })}
          </div>
        )}
        <InfinitePagination />
      </div>
    </TranscriptMatchesProvider>
  );
};
